import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchCollabPendingQuizzes,
} from "../../vsecurite/vsecuriteSlice";
import Langs, { Localize } from "../../../app/lang/langs";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";

/* ── Palette constants (aligned with CollabMcqView) ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const GREEN = "#16a34a";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/* ── Fade-in animation injected once ── */
const KEYFRAMES = `
    @keyframes qcFadeInUp {
        from { opacity: 0; transform: translateY(14px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

/**
 * Formats a deadline date string into a readable French locale format.
 * Returns null when no deadline is provided.
 *
 * @param {string|null} deadline - ISO date string or null
 * @returns {string|null}
 */
function formatDeadline(deadline) {
    if (!deadline) return null;
    const d = new Date(deadline);
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

/**
 * QuizCollab – Displays the list of quizzes pending completion for a collab.
 *
 * On mount it dispatches fetchCollabPendingQuizzes to populate
 * state.vsecurite.quizzes.collabPending.
 *
 * @param {{ onStartQuiz: (quiz: object) => void }} props
 */
export default function QuizCollab({ onStartQuiz }) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));

    /** @type {Array<{id: number, title: string, description: string, passing_score: number, published_at: string|null, deadline: string|null, attempts_count: number, best_score: number|null}>} */
    const collabPending = useSelector((state) => state.vsecurite.quizzes.collabPending);
    const loading = useSelector((state) => state.vsecurite.quizzes.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);
    const mobile = isMobileRedux === 2 || isMobileScreen;

    /* Fetch pending quizzes when the component mounts */
    useEffect(() => {
        dispatch(fetchCollabPendingQuizzes());
    }, [dispatch]);

    /* ── Loading skeleton ── */
    if (loading && collabPending.length === 0) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress sx={{ color: ORANGE }} />
            </Box>
        );
    }

    /* ── Empty state ── */
    if (collabPending.length === 0) {
        return (
            <>
                <style>{KEYFRAMES}</style>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        py: mobile ? 6 : 10,
                        gap: 2,
                        animation: `qcFadeInUp 500ms ${EASE} both`,
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "20px",
                            background: ORANGE_LIGHT,
                            border: "1px solid rgba(255,144,21,0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                        }}
                    >
                        <EmojiEventsOutlinedIcon sx={{ fontSize: 32, color: ORANGE }} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: "1.1rem",
                            fontWeight: 650,
                            color: TEXT_PRIMARY,
                            letterSpacing: "-0.02em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            textAlign: "center",
                        }}
                    >
                        <Langs str="VSEC_COLLAB_QUIZ_EMPTY" />
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "0.9rem",
                            color: TEXT_MUTED,
                            fontFamily: "'Inter',sans-serif",
                            textAlign: "center",
                        }}
                    >
                        <Langs str="VSEC_COLLAB_QUIZ_CONGRATS" />
                    </Typography>
                </Box>
            </>
        );
    }

    return (
        <>
            <style>{KEYFRAMES}</style>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: mobile ? 2.5 : 3,
                    px: mobile ? 0 : undefined,
                }}
            >
                {collabPending.map((quiz, idx) => (
                    <QuizPendingCard
                        key={quiz.id}
                        quiz={quiz}
                        animationDelay={idx * 70}
                        onStartQuiz={onStartQuiz}
                        lang={lang}
                        mobile={mobile}
                    />
                ))}
            </Box>
        </>
    );
}

/**
 * QuizPendingCard – Single card representing one pending quiz.
 *
 * Displays title, description, minimum score, deadline (when present),
 * attempt count and best score. Shows "Commencer le Quiz" for a first
 * attempt, or "Réessayer" for subsequent attempts.
 *
 * @param {{
 *   quiz: {id: number, title: string, description: string, passing_score: number, deadline: string|null, attempts_count: number, best_score: number|null},
 *   animationDelay: number,
 *   onStartQuiz: (quiz: object) => void,
 *   mobile: boolean
 * }} props
 */
function QuizPendingCard({ quiz, animationDelay, onStartQuiz, lang, mobile }) {
    const hasAttempts = quiz.attempts_count > 0;
    const deadline = formatDeadline(quiz.deadline);
    const isDeadlinePassed = quiz.deadline && new Date(quiz.deadline) < new Date();
    const isSingleTakeUsed = !quiz.allow_multiple_takes && hasAttempts;

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: mobile ? "14px" : "18px",
                border: `1px solid ${BORDER_SUBTLE}`,
                background: "#ffffff",
                minHeight: mobile ? undefined : 260,
                animation: `qcFadeInUp 500ms ${EASE} ${animationDelay}ms both`,
                transition: `all 300ms ${EASE}`,
                overflow: "hidden",
                position: "relative",
                /* top orange accent bar */
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: ORANGE_GRADIENT,
                },
                "&:hover": {
                    boxShadow: mobile ? undefined : "0 4px 24px rgba(255,144,21,0.10), 0 1px 6px rgba(0,0,0,0.04)",
                    transform: mobile ? undefined : "translateY(-2px)",
                },
                "&:active": mobile ? { transform: "scale(0.99)" } : undefined,
            }}
        >
            <CardContent sx={{ pt: 3, pb: 2, px: mobile ? 2.5 : undefined }}>
                {/* Quiz title */}
                <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "10px",
                            background: ORANGE_LIGHT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            mt: 0.2,
                        }}
                    >
                        <QuizOutlinedIcon sx={{ fontSize: 18, color: ORANGE }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: "1rem",
                                fontWeight: 650,
                                color: TEXT_PRIMARY,
                                letterSpacing: "-0.02em",
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                lineHeight: 1.3,
                            }}
                        >
                            {quiz.title}
                        </Typography>
                    </Box>
                </Stack>

                {/* Description */}
                {quiz.description ? (
                    <Typography
                        sx={{
                            fontSize: "0.88rem",
                            color: TEXT_SECONDARY,
                            fontFamily: "'Inter',sans-serif",
                            lineHeight: 1.55,
                            mb: 2,
                            pl: mobile ? 0 : "46px",
                        }}
                    >
                        {quiz.description}
                    </Typography>
                ) : null}

                <Divider sx={{ mb: 2, borderColor: BORDER_SUBTLE }} />

                {/* Meta chips row */}
                <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ mb: hasAttempts ? 2 : 1 }}
                >
                    {/* Minimum score chip */}
                    <MetaChip
                        label={`${Localize("VSEC_QUIZ_META_MIN_SCORE", lang)} : ${quiz.min_score ?? quiz.passing_score ?? '—'}%`}
                        color={ORANGE}
                        bgColor="rgba(255,144,21,0.08)"
                        borderColor="rgba(255,144,21,0.18)"
                    />

                    {/* Deadline chip (only when a deadline is set) */}
                    {deadline ? (
                        <MetaChip
                            icon={<EventOutlinedIcon sx={{ fontSize: 13 }} />}
                            label={`${Localize("VSEC_QUIZ_META_DEADLINE", lang)} : ${deadline}`}
                            color="#c2410c"
                            bgColor="rgba(194,65,12,0.07)"
                            borderColor="rgba(194,65,12,0.15)"
                        />
                    ) : null}
                </Stack>

                {/* Attempt history row (shown only after at least one attempt) */}
                {hasAttempts ? (
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        <MetaChip
                            label={`${quiz.attempts_count} ${Localize("VSEC_QUIZ_META_ATTEMPTS", lang)}`}
                            color={TEXT_SECONDARY}
                            bgColor="rgba(0,0,0,0.04)"
                            borderColor="rgba(0,0,0,0.08)"
                        />
                        {quiz.allow_scores && quiz.best_score !== null && quiz.best_score !== undefined ? (
                            <MetaChip
                                label={`${Localize("VSEC_QUIZ_META_BEST_SCORE", lang)} : ${quiz.best_score}%`}
                                color={GREEN}
                                bgColor="rgba(22,163,74,0.07)"
                                borderColor="rgba(22,163,74,0.15)"
                            />
                        ) : null}
                    </Stack>
                ) : null}
            </CardContent>

            <CardActions sx={{ px: mobile ? 2.5 : 2.5, pb: 2.5, pt: 0 }}>
                {isSingleTakeUsed ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{
                        px: 2, py: 0.9, borderRadius: "12px",
                        background: "rgba(0,0,0,0.04)",
                        border: "1px solid rgba(0,0,0,0.10)",
                        width: mobile ? "100%" : undefined,
                        justifyContent: mobile ? "center" : undefined,
                    }}>
                        <EventBusyRoundedIcon sx={{ fontSize: 17, color: TEXT_MUTED }} />
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: TEXT_MUTED, fontFamily: "'Inter',sans-serif" }}>
                            {Localize("VSEC_SINGLE_TAKE_USED", lang)}
                        </Typography>
                    </Stack>
                ) : isDeadlinePassed ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{
                        px: 2, py: 0.9, borderRadius: "12px",
                        background: "rgba(220,38,38,0.06)",
                        border: "1px solid rgba(220,38,38,0.18)",
                        width: mobile ? "100%" : undefined,
                        justifyContent: mobile ? "center" : undefined,
                    }}>
                        <EventBusyRoundedIcon sx={{ fontSize: 17, color: "#dc2626" }} />
                        <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#dc2626", fontFamily: "'Inter',sans-serif" }}>
                            {Localize("VSEC_DEADLINE_PASSED", lang)}
                        </Typography>
                    </Stack>
                ) : (
                    <Button
                        variant="contained"
                        onClick={() => onStartQuiz(quiz)}
                        fullWidth={mobile}
                        startIcon={
                            hasAttempts
                                ? <ReplayRoundedIcon sx={{ fontSize: "18px !important" }} />
                                : <PlayArrowRoundedIcon sx={{ fontSize: "18px !important" }} />
                        }
                        sx={{
                            borderRadius: "12px",
                            px: 3,
                            py: 0.75,
                            textTransform: "none",
                            fontWeight: 650,
                            fontSize: "0.88rem",
                            letterSpacing: "-0.01em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            background: ORANGE_GRADIENT,
                            color: "#fff",
                            minHeight: 42,
                            boxShadow: "0 3px 12px rgba(255,144,21,0.25)",
                            transition: `all 280ms ${EASE}`,
                            "&:hover": {
                                background: ORANGE_GRADIENT,
                                boxShadow: "0 5px 20px rgba(255,144,21,0.35)",
                                transform: "translateY(-1px)",
                            },
                            "&:active": { transform: "scale(0.98)" },
                        }}
                    >
                        {hasAttempts ? Localize("VSEC_BTN_RETRY_QUIZ", lang) : Localize("VSEC_BTN_START_QUIZ", lang)}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
}

/**
 * MetaChip – A small inline info chip used to display quiz metadata.
 *
 * @param {{ label: string, icon?: React.ReactNode, color: string, bgColor: string, borderColor: string }} props
 */
function MetaChip({ label, icon, color, bgColor, borderColor }) {
    return (
        <Chip
            icon={icon || undefined}
            label={label}
            size="small"
            sx={{
                fontSize: "0.78rem",
                fontWeight: 550,
                fontFamily: "'Inter',sans-serif",
                color,
                background: bgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: "8px",
                height: 30,
                "& .MuiChip-label": { px: 1.4 },
                "& .MuiChip-icon": { color, ml: 0.8 },
            }}
        />
    );
}
